import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DISPLAY_HEADER_ICON_BACK,
  DISPLAY_PAGE_CHOOSE_MALFUNCTION,
  LANGUAGE_ENGLISH,
  LANGUAGE_JAPANESE,
  PAGE_LINK_ERR_CORD_SEARCH,
  FILTER_ITEM,
  FILTER_CRITERIA,
} from "../../constants/common_constants";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import { PhenomenonAllList } from "../../features/components/select_phenomenon/ui_elements/PhenomenonAllList";
import { calcUtcDatetime } from "../../utils/basis_util";
import { PhenomenonNextPageButton } from "../../features/components/select_phenomenon/ui_elements/PhenomenonNextPageButton";
import { useDtsAppContext } from "../provider/DtsAppContextProvider";
import { IPhenomenonType } from "../../features/components/select_phenomenon/types/phenomenon_type";
import { useApi, useErrorDialog, useInitUsageLog } from "../../hooks";
import { useTranslation } from "react-i18next";
import { getPhenomenon } from "../../features/api/dts_api_wrapper";

export const SelectPhenomenon = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();
  const { handleApi } = useApi();

  const {
    dacSubjectName,
    dacModel,
    dacMachineNumber,
    dacDtcSearchData,
    dacLanguage,
    dacModelType,
    dacOccurrenceTiming,
  } = useDtsAppContext();
  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
    ) || "";
  const subjectName =
    dacSubjectName || localStorage.getItem("subjectName") || "";
  const model = dacModel || localStorage.getItem("model") || "";
  const dtcSearchData =
    dacDtcSearchData || localStorage.getItem("dtcSearchData") || "";
  const modelType = dacModelType || localStorage.getItem("modelType") || "";
  const machineNumber =
    dacMachineNumber || localStorage.getItem("machineNumber") || "";
  const displayPage = DISPLAY_PAGE_CHOOSE_MALFUNCTION;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)
  const language = dacLanguage || localStorage.getItem("language") || ""; //言語
  const occurrenceTiming =
    Number(localStorage.getItem("occurrenceTiming")) ||
    dacOccurrenceTiming ||
    NaN;

  // 初期表示時の利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });
  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  // 選択された現象をboolean配列で格納
  const [selectedPhenomenonGroup, setSelectedPhenomenonGroup] = useState<
    boolean[]
  >([]);

  // 選択された現象名をstring配列で格納
  const [selectedPhenomenonName, setSelectedPhenomenonName] = useState<
    string[]
  >([]);

  // 取得した現象リストを格納, 空のオブジェクトを初期値として設定
  const [phenomenonList, setPhenomenonList] = useState<IPhenomenonType>({
    phenomenon: {
      language: "",
      phenomenons: [],
    },
  });

  //ページ表示時に行うPhenomenonSelectionの処理
  const handlePhenomenonSelectionInit = async () => {
    try {
      console.log("handlePhenomenonSelectionInit start");
      //userIdが読み込めなかった際の処理は, intUsageLog()に記述
      //利用ログ登録
      const isError = await initUsageLog();
      if (isError) {
        return;
      }

      //推定要因項目画面のフィルタ条件をクリア
      localStorage.removeItem(FILTER_ITEM);
      localStorage.removeItem(FILTER_CRITERIA);

      //現象取得
      const phenomenonResult = await handleApi(
        getPhenomenon,
        userId,
        occurrenceTiming,
        language,
      );
      //現象取得エラーの場合, トップページに遷移
      if ("errorMessage" in phenomenonResult) {
        console.log("errorMessage: ", phenomenonResult.errorMessage);
        throw new Error("failed to get phenomenon");
      }
      //データが空かどうか判別, エラーダイアログ表示(トップページに遷移)
      const phenomenonToJson: IPhenomenonType = JSON.parse(
        JSON.stringify(phenomenonResult),
      );
      if (!phenomenonToJson.phenomenon.phenomenons.length) {
        throw new Error("phenomenon is empty");
      }

      //取得した現象をpriorityでソートしStateに保存
      sortPhenomenonData(phenomenonResult);
      const phenomenonStr = JSON.stringify(phenomenonResult);
      setPhenomenonList(JSON.parse(phenomenonStr));
      //取得した現象リストの数に応じたboolean配列を生成, selectedMenuに格納
      setSelectedPhenomenonGroup(
        JSON.parse(phenomenonStr).phenomenon.phenomenons.map(() => false),
      );
    } catch (err) {
      console.log(`[${displayPage}]err: `, err);
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage",
      ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
    }
  };

  useEffect(() => {
    // 言語がなければトップページへ遷移
    if (!language) {
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage",
      );
    } else {
      handlePhenomenonSelectionInit();
      const handleStorageChange = (event: StorageEvent) => {
        if (
          event.key ===
          `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
        ) {
          const authUser = localStorage.getItem(
            `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
          );
          if (!authUser) {
            console.log(`[${displayPage}]err: userId is unknown`);
            showErrorDialog(
              t("共通エラーポップ.エラー"),
              t("共通エラーポップ.通信エラーが発生しました。"),
              "navigateToTopPage",
            ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
          }
        }
      };
      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener("storage", handleStorageChange);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, []);

  //リストをレンダリングする関数
  const renderAllList = () => {
    return phenomenonList.phenomenon.phenomenons.map((phenomenons, index) => (
      <PhenomenonAllList
        phenomenons={phenomenons}
        phenomenonNumber={index}
        setSelectedPhenomenonGroup={setSelectedPhenomenonGroup}
        setSelectedPhenomenonName={setSelectedPhenomenonName}
        key={phenomenons.question1.label}
      />
    ));
  };

  return (
    // スクロール不可に設定
    <div className="flex flex-col h-svh overflow-hidden">
      {/* header */}
      <GeneralCommonHeader
        title={t("現象選択画面.現象選択")} //現象選択
        displayheaderIcon={DISPLAY_HEADER_ICON_BACK}
        onBtnClick={() => {
          navigate(-1);
        }}
      />
      {/* header以下 */}
      <div className="flex flex-col m-4 h-full overflow-hidden">
        {/* 選択指示 */}
        <div className="mb-2">
          <p className="pb-2 ">
            {t("現象選択画面.不具合をすべて選択してください")}
            {/* 不具合をすべて選択してください */}
          </p>
          <p className="pb-2">
            {/* エラーコードサーチ(外部リンク)を別タブで開く */}
            {language === LANGUAGE_JAPANESE && t("現象選択画面.DTCの検索は")}
            <Link
              to={`${PAGE_LINK_ERR_CORD_SEARCH}${
                dtcSearchData ? `${dtcSearchData}/` : ""
              }`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sub-blue underline"
            >
              {t("現象選択画面.コチラ")} {/* コチラ */}
            </Link>
            {language === LANGUAGE_ENGLISH && t("現象選択画面.DTCの検索は")}
            {/* エラーコードの検索は */}
          </p>
        </div>
        {/* 現象選択 */}
        <div className="h-full overflow-y-scroll overflow-x-hidden">
          {/* 現象リスト表示 */}
          {renderAllList()}
          {/* ボタン */}
          <PhenomenonNextPageButton
            disabled={!selectedPhenomenonGroup.some((value) => value)}
            userId={userId}
            subjectName={subjectName}
            model={model}
            occurrenceTiming={occurrenceTiming}
            phenomenon={selectedPhenomenonName}
            language={language}
            machineNumber={machineNumber}
            modelType={modelType}
          />
        </div>
      </div>
    </div>
  );
};

/* 取得した現象リストをpriorityに従ってソートする関数 */
const sortPhenomenonData = (unsortedData: IPhenomenonType) => {
  unsortedData.phenomenon.phenomenons.sort((a, b) =>
    //前(a)の値が後(b)の値より小さい時, aとbの順番はそのまま.
    a.question1.priority < b.question1.priority ? -1 : 1,
  );
  unsortedData.phenomenon.phenomenons.map((phenomenons) => {
    return phenomenons.question1.question2.sort((a, b) =>
      //前(a)の値が後(b)の値より小さい時, aとbの順番はそのまま.
      a.priority < b.priority ? -1 : 1,
    );
  });
  return unsortedData;
};
